import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, CircularProgress, IconButton, Tooltip,
  FormControl, InputLabel, Select, MenuItem, Divider, useTheme
} from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, LineChart, Line, Cell, Legend, LabelList
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../../config/axio.config';
import IncomeExpenseBreakdown from "../../../../components/financeiro/incomeExpenseBreakdown/incomeExpenseBreakdown";
import DailyTimeline from "../../../../components/financeiro/dailyTimeline/dailyTimeline";
import MonthlyTimeline from "../../../../components/financeiro/monthlyTimeline/monthlyTimeline";
import SalesBreakdown from "../../../../components/financeiro/salesBreakdown/salesBreakdown";
import SalesTypeBreakdown from "../../../../components/financeiro/salesTypeBreakdown/salesTypeBreakdown";
import CanceledBreakdown from "../../../../components/financeiro/canceledBreakdown/canceledBreakdown";
import GrowthComparison from "../../../../components/financeiro/growthComparison/growthComparison";
import ExpenseClassificationDialog from "../../../../components/financeiro/expenseClassificationDialog/expenseClassificationDialog";

const COLORS = [
  '#42a5f5', '#ef5350', '#66bb6a', '#ffa726', '#ce93d8',
  '#26c6da', '#bcaaa4', '#ec407a', '#9fa8da', '#29b6f6',
  '#d4e157', '#ff7043', '#b0bec5',
];

// `id` is the chart's stable identifier; `value`/`label` are just the select numbering
const OPTIONS = [
  { value: 1, id: "incomeExpense", label: '1 - Receitas x Despesas' },
  { value: 2, id: "dailyTimeline", label: '2 - Receitas x Despesas (Linha do Tempo)' },
  { value: 3, id: "monthlyEvolution", label: '3 - Evolução Mensal (Receitas x Despesas x Saldo)' },
  { value: 4, id: "projection", label: '4 - Projeção Futura (Receitas x Despesas)' },
  { value: 5, id: "topSuppliers", label: '5 - Top Fornecedores por Despesa' },
  { value: 6, id: "topClients", label: '6 - Top Clientes por Receita' },
  { value: 7, id: "salesPerformed", label: '7 - Vendas Realizadas (Segmento x Forma de Pagamento)' },
  { value: 8, id: "salesTypeOrigin", label: '8 - Vendas Realizadas (Tipo x Origem da Venda)' },
  { value: 9, id: "companyGrowth", label: '9 - Crescimento da Empresa (Vendas x Cancelamentos)' },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatCurrencyShort = (value) => {
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return value.toFixed(0);
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5 }} elevation={3}>
      <Typography variant="body2" fontWeight="bold">{label}</Typography>
      {payload.map((entry, i) => (
        <Typography key={i} variant="body2" sx={{ color: entry.color }}>
          {entry.name}: {formatCurrency(entry.value)}
        </Typography>
      ))}
    </Paper>
  );
};

// Y-axis tick for the top-clients chart: client name plus the sum of both bars
const ClientTotalTick = ({ x, y, payload, data, primary, muted }) => {
  const client = data.find((c) => c.clientName === payload.value);
  const name = payload.value.length > 30 ? `${payload.value.slice(0, 29)}…` : payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{payload.value}</title>
      <text x={-8} y={-2} textAnchor="end" fontSize={11} fill={primary}>{name}</text>
      <text x={-8} y={11} textAnchor="end" fontSize={10} fill={muted}>
        Total: {formatCurrency(client?.total || 0)}
      </text>
    </g>
  );
};

const TopClientTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const client = payload[0].payload;
  return (
    <Paper sx={{ p: 1.5 }} elevation={3}>
      <Typography variant="body2" fontWeight="bold">{client.clientName}</Typography>
      <Typography variant="body2" sx={{ color: '#42a5f5' }}>Venda: {formatCurrency(client.sales)}</Typography>
      <Typography variant="body2" sx={{ color: '#66bb6a' }}>Manutenção: {formatCurrency(client.maintenance)}</Typography>
      <Typography variant="body2" fontWeight="bold">Total: {formatCurrency(client.total)}</Typography>
      {client.receivedTotal > client.total && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Outras receitas no período: {formatCurrency(client.receivedTotal - client.total)}
        </Typography>
      )}
    </Paper>
  );
};

export default function FinanceiroReportsPage() {
  const theme = useTheme();
  const [option, setOption] = useState(1);
  // Filters for the monthly evolution tab
  const [monthStart, setMonthStart] = useState(dayjs().subtract(5, 'month'));
  const [monthEnd, setMonthEnd] = useState(dayjs());
  // Filters for the date-range charts
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  // The projection has its own month range so it does not interfere with the other tabs.
  // Default: from the current month through the month covering the next 90 days.
  const [projStart, setProjStart] = useState(dayjs());
  const [projEnd, setProjEnd] = useState(dayjs().add(90, 'day'));
  const [loading, setLoading] = useState(false);

  const [evolution, setEvolution] = useState([]);
  const [dailyEvolution, setDailyEvolution] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [incomes, setIncomes] = useState(null);
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const [canceled, setCanceled] = useState(null);
  const [salesPerformed, setSalesPerformed] = useState(null);
  const [projection, setProjection] = useState(null);
  const [growth, setGrowth] = useState(null);
  // Edicao de um lancamento pago (descricao + centro de custos), aberta pelo
  // detalhamento das despesas do Grafico 1
  const [costCenters, setCostCenters] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);

  const currentOption = OPTIONS.find((o) => o.value === option) || OPTIONS[0];
  const optionId = currentOption.id;

  // Evolution uses a month range, projection has its own, the rest use start/end dates
  const isMonthlyEvolution = optionId === "monthlyEvolution";
  const isProjection = optionId === "projection";
  // Tabs that grow with their content (list/table below the chart) instead of a fixed height
  const isAutoHeight = optionId === "incomeExpense"
    || optionId === "dailyTimeline"
    || optionId === "monthlyEvolution"
    || optionId === "projection"
    || optionId === "salesPerformed"
    || optionId === "salesTypeOrigin"
    || optionId === "companyGrowth";

  // Entering the projection always resets to the next 90 days
  useEffect(() => {
    if (optionId === "projection") {
      setProjStart(dayjs());
      setProjEnd(dayjs().add(90, 'day'));
    } else if (optionId === "monthlyEvolution") {
      setMonthStart(dayjs().subtract(5, 'month'));
      setMonthEnd(dayjs());
    }
  }, [optionId]);

  const evolutionRange = useMemo(() => ({
    startDate: monthStart.startOf('month').format('YYYY-MM-DD'),
    endDate: monthEnd.endOf('month').format('YYYY-MM-DD'),
  }), [monthStart, monthEnd]);

  const detailRange = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
  }), [startDate, endDate]);

  // Always whole months: the monthly breakdown queries the closed month, so a partial
  // range would not reconcile with the chart line
  const projectionRange = useMemo(() => ({
    startDate: projStart.startOf('month').format('YYYY-MM-DD'),
    endDate: projEnd.endOf('month').format('YYYY-MM-DD'),
  }), [projStart, projEnd]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [evoRes, dayRes, expRes, incRes, supRes, cliRes, canRes, salRes, projRes, growRes] = await Promise.all([
        axiosInstance.get('/financeiro/reports/monthly-evolution', { params: evolutionRange }),
        axiosInstance.get('/financeiro/reports/daily-evolution', { params: detailRange }),
        axiosInstance.get('/financeiro/expenses', { params: detailRange }),
        axiosInstance.get('/financeiro/incomes', { params: detailRange }),
        axiosInstance.get('/financeiro/reports/top-suppliers', { params: { ...detailRange, limit: 10 } }),
        axiosInstance.get('/financeiro/reports/top-clients', { params: { ...detailRange, limit: 10 } }),
        axiosInstance.get('/financeiro/reports/canceled-clients', { params: detailRange }),
        axiosInstance.get('/financeiro/reports/sales-performed', { params: detailRange }),
        axiosInstance.get('/financeiro/reports/projection', { params: projectionRange }),
        axiosInstance.get('/financeiro/reports/growth', { params: detailRange }),
      ]);
      setEvolution(evoRes.data.evolution || []);
      setDailyEvolution(dayRes.data);
      setExpenses(expRes.data);
      setIncomes(incRes.data);
      setTopSuppliers(supRes.data.suppliers || []);
      setTopClients(cliRes.data.clients || []);
      setCanceled(canRes.data);
      setSalesPerformed(salRes.data);
      setProjection(projRes.data);
      setGrowth(growRes.data);
    } catch (err) {
      console.error('Erro ao buscar dados de relatorios', err);
    } finally {
      setLoading(false);
    }
  }, [evolutionRange, detailRange, projectionRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // O cadastro de centros de custos nao depende do periodo: carrega uma vez e serve
  // a todas as edicoes. Falhar aqui nao derruba o relatorio — so a edicao fica sem lista.
  useEffect(() => {
    axiosInstance.get('/financeiro/cost-centers')
      .then((res) => setCostCenters(res.data || []))
      .catch((err) => console.error('Erro ao buscar centros de custos', err));
  }, []);

  // Forward projection (incomes x expenses by due month)
  const projectionData = useMemo(() => projection?.projection || [], [projection]);
  const projectionTotals = useMemo(() => projection?.totals || { incomes: 0, expenses: 0, balance: 0 }, [projection]);
  const projectionPayroll = useMemo(() => projection?.payroll?.monthlyAverage || 0, [projection]);
  const projectionPayrollMonths = useMemo(() => projection?.payroll?.months || 6, [projection]);

  const rangeLabel = isMonthlyEvolution
    ? `${monthStart.locale('pt-br').format('MMM/YYYY')} - ${monthEnd.locale('pt-br').format('MMM/YYYY')}`
    : isProjection
      ? `${projStart.locale('pt-br').format('MMM/YYYY')} - ${projEnd.locale('pt-br').format('MMM/YYYY')}`
      : `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;

  const renderEmpty = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
      <Typography color="text.secondary">Sem dados no período</Typography>
    </Box>
  );

  const renderChart = () => {
    switch (optionId) {
      case "incomeExpense":
        return (
          <IncomeExpenseBreakdown
            incomes={incomes}
            expenses={expenses}
            onExpenseClick={setEditingExpense}
          />
        );

      // Daily timeline: same series as the monthly evolution but day by day,
      // with the entries table right below
      case "dailyTimeline": {
        const daily = dailyEvolution?.daily || [];
        if (!daily.length) return renderEmpty();
        return (
          <Box>
            <Box sx={{ height: 420 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 20, right: 28, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
                  <YAxis
                    tickFormatter={formatCurrencyShort}
                    domain={[
                      (dataMin) => Math.min(0, Math.floor(dataMin * 1.15)),
                      (dataMax) => Math.ceil(dataMax * 1.15),
                    ]}
                  />
                  <RTooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="incomes" name="Receitas" stroke="#66bb6a" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="expenses" name="Despesas" stroke="#ef5350" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="runningBalance" name="Saldo acumulado" stroke="#42a5f5" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <DailyTimeline days={daily} totals={dailyEvolution?.totals} />
          </Box>
        );
      }

      case "monthlyEvolution":
        return (
          <Box>
            <Box sx={{ height: 420 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolution} margin={{ top: 28, right: 28, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="label" />
                  <YAxis
                    tickFormatter={formatCurrencyShort}
                    domain={[
                      (dataMin) => Math.min(0, Math.floor(dataMin * 1.15)),
                      (dataMax) => Math.ceil(dataMax * 1.15),
                    ]}
                  />
                  <RTooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="incomes" name="Receitas" stroke="#66bb6a" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList dataKey="incomes" position="top" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#66bb6a', fontWeight: 'bold' }} />
                  </Line>
                  <Line type="monotone" dataKey="expenses" name="Despesas" stroke="#ef5350" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList dataKey="expenses" position="bottom" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#ff6b6b', fontWeight: 'bold' }} />
                  </Line>
                  <Line type="monotone" dataKey="balance" name="Saldo" stroke="#42a5f5" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }}>
                    <LabelList dataKey="balance" position="top" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#42a5f5', fontWeight: 'bold' }} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <MonthlyTimeline months={evolution} />
          </Box>
        );

      case "topSuppliers":
        if (!topSuppliers.length) return renderEmpty();
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topSuppliers} margin={{ top: 24, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="supplierName" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11 }} height={80} />
              <YAxis tickFormatter={formatCurrencyShort} />
              <RTooltip content={<ChartTooltip />} />
              <Bar dataKey="totalPaid" name="Total Pago" radius={[6, 6, 0, 0]} barSize={40}>
                {topSuppliers.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
                <LabelList dataKey="totalPaid" position="top" formatter={formatCurrencyShort} style={{ fontSize: 11, fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      // Barras horizontais: nomes de cliente sao longos demais para o eixo X.
      // Cada cliente tem uma barra de venda e uma de manutencao; o total vem no eixo.
      case "topClients":
        if (!topClients.length) return renderEmpty();
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={topClients} layout="vertical" margin={{ left: 8, right: 72, top: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
              <XAxis type="number" tickFormatter={formatCurrencyShort} />
              <YAxis
                type="category"
                dataKey="clientName"
                width={210}
                interval={0}
                tick={<ClientTotalTick data={topClients} primary={theme.palette.text.primary} muted={theme.palette.text.secondary} />}
              />
              <RTooltip content={<TopClientTooltip />} />
              <Legend />
              <Bar dataKey="sales" name="Venda" fill="#42a5f5" radius={[0, 4, 4, 0]} barSize={9}>
                <LabelList dataKey="sales" position="right" formatter={formatCurrencyShort} style={{ fontSize: 10, fontWeight: 'bold', fill: '#42a5f5' }} />
              </Bar>
              <Bar dataKey="maintenance" name="Manutenção" fill="#66bb6a" radius={[0, 4, 4, 0]} barSize={9}>
                <LabelList dataKey="maintenance" position="right" formatter={formatCurrencyShort} style={{ fontSize: 10, fontWeight: 'bold', fill: '#66bb6a' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      // Two readings of the same movement: what was lost and why (donut), and the
      // balance against what was sold (bars below it)
      case "companyGrowth":
        if (!canceled?.byReason?.totalCount && !growth?.newClients?.count) return renderEmpty();
        return (
          <Box>
            <CanceledBreakdown canceled={canceled} />
            <Divider sx={{ my: 3 }} />
            <GrowthComparison growth={growth} />
          </Box>
        );

      case "salesPerformed":
        if (!salesPerformed?.bySegment?.totalCount) return renderEmpty();
        return <SalesBreakdown sales={salesPerformed} />;

      // Same sales as the tab above, read by what was sold and where it came from
      case "salesTypeOrigin":
        if (!salesPerformed?.byType?.totalCount) return renderEmpty();
        return <SalesTypeBreakdown sales={salesPerformed} />;

      case "projection": {
        if (!projectionData.length) return renderEmpty();
        const balanceColor = (projectionTotals.balance || 0) >= 0 ? '#66bb6a' : '#ff6b6b';
        return (
          <Box>
            <Typography variant="subtitle2" align="center" fontWeight="bold" sx={{ color: balanceColor }}>
              Previsto no período &nbsp;·&nbsp; Receitas: {formatCurrency(projectionTotals.incomes || 0)}
              &nbsp;·&nbsp; Despesas: {formatCurrency(projectionTotals.expenses || 0)}
              &nbsp;·&nbsp; Saldo: {formatCurrency(projectionTotals.balance || 0)}
            </Typography>
            <Typography variant="caption" align="center" display="block" color="text.secondary">
              As despesas incluem a <strong>folha de pagamento</strong> (salários, benefícios e encargos de pessoal),
              estimada pela média dos últimos {projectionPayrollMonths} meses: {formatCurrency(projectionPayroll)}/mês
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={projectionData} margin={{ top: 28, right: 28, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
                  <XAxis dataKey="label" />
                  <YAxis
                    tickFormatter={formatCurrencyShort}
                    domain={[
                      (dataMin) => Math.min(0, Math.floor(dataMin * 1.15)),
                      (dataMax) => Math.ceil(dataMax * 1.15),
                    ]}
                  />
                  <RTooltip content={<ChartTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="incomes" name="Receitas previstas" stroke="#66bb6a" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList dataKey="incomes" position="top" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#66bb6a', fontWeight: 'bold' }} />
                  </Line>
                  <Line type="monotone" dataKey="expenses" name="Despesas previstas" stroke="#ef5350" strokeWidth={2} dot={{ r: 4 }}>
                    <LabelList dataKey="expenses" position="bottom" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#ff6b6b', fontWeight: 'bold' }} />
                  </Line>
                  <Line type="monotone" dataKey="balance" name="Saldo previsto" stroke="#42a5f5" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 4 }}>
                    <LabelList dataKey="balance" position="top" formatter={formatCurrencyShort} style={{ fontSize: 10, fill: '#42a5f5', fontWeight: 'bold' }} />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <MonthlyTimeline
              months={projectionData}
              title="Detalhamento mensal previsto"
              endpoint="/financeiro/reports/projection-detail"
              emptyLabel="Nada previsto no período"
            />
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">
          Relatorios Financeiros
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Grafico</InputLabel>
              <Select
                label="Grafico"
                value={option}
                onChange={(e) => setOption(e.target.value)}
              >
                {OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              {isMonthlyEvolution ? (
                <DatePicker
                  views={['month', 'year']}
                  label="Mes Inicio"
                  value={monthStart}
                  onChange={(v) => v && setMonthStart(v)}
                  maxDate={monthEnd}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              ) : isProjection ? (
                <DatePicker
                  views={['month', 'year']}
                  label="Mês Início"
                  value={projStart}
                  onChange={(v) => v && setProjStart(v)}
                  maxDate={projEnd}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              ) : (
                <DatePicker
                  label="Data Inicio"
                  value={startDate}
                  onChange={(v) => v && setStartDate(v)}
                  maxDate={endDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              )}
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
              {isMonthlyEvolution ? (
                <DatePicker
                  views={['month', 'year']}
                  label="Mes Fim"
                  value={monthEnd}
                  onChange={(v) => v && setMonthEnd(v)}
                  minDate={monthStart}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              ) : isProjection ? (
                <DatePicker
                  views={['month', 'year']}
                  label="Mês Fim"
                  value={projEnd}
                  onChange={(v) => v && setProjEnd(v)}
                  minDate={projStart}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              ) : (
                <DatePicker
                  label="Data Fim"
                  value={endDate}
                  onChange={(v) => v && setEndDate(v)}
                  minDate={startDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              )}
            </LocalizationProvider>
          </Grid>
        </Grid>
      </Paper>

      {/* Grafico — a visao consolidada cresce conforme a lista; as demais tem altura fixa */}
      <Paper sx={{ p: 2, ...(isAutoHeight ? { minHeight: 500 } : { height: 500 }) }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom align="center">
          {currentOption.label} - {rangeLabel}
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: isAutoHeight ? 'auto' : '90%' }}>
            {renderChart()}
          </Box>
        )}
      </Paper>

      {/* Salvar reclassifica a despesa e o relatorio e refeito: o lancamento pode
          ter mudado de centro de custos, ou seja, de grupo e de fatia do grafico */}
      <ExpenseClassificationDialog
        open={!!editingExpense}
        expense={editingExpense}
        costCenters={costCenters}
        onClose={() => setEditingExpense(null)}
        onSaved={fetchData}
      />
    </Box>
  );
}
